import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan, ToolbarAddMenu } from 'components/enterprise-shell';
import DocumentNumberField from 'components/common/DocumentNumberField';
import notificationService from 'services/notificationService';
import { useNavTarget } from 'contexts/NavigationContext';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useSalesNotesRegister, useSalesLineLedgers, useSalesServiceLedgers } from 'hooks/api/useCommercial';
import {
  InvoiceFormModal, InvoiceViewModal,
  ITEM_INCOME_LEDGER_MISSING, SERVICE_INCOME_LEDGER_MISSING, serviceLedgerSourceTag, type ResolvedLineLedger,
} from './SalesInvoiceScreen';
import { creditNoteService, salesInvoiceService, partyService, serviceService, voucherTypeService, taxMetaService, taxProfileService, entityAccountingConfigService, currencyService, coaLedgerService } from 'api/services';
import TaxFieldsPanel, { validateTaxFields, type TaxFieldValues } from 'components/modules/tax/TaxFieldsPanel';
import SetupGateModal, { useSetupGate } from 'components/modules/setup/SetupGateModal';
import {
  useItemLineLookups, blankItemFieldValues, itemFieldValuesOf, itemLabel, ledgerSourceTag,
  ItemPickerCell, ItemLineDetails, ItemLineSummary, ReadOnlyLedgerValue, type ItemFields,
} from 'components/modules/shared/itemLines';
import type { CreditNoteDto, CreditNoteLineDto, SalesNoteRegisterRowDto, SalesNoteRegisterQueryParams, SalesInvoiceDto, SalesInvoiceLineDto, CreateCreditNoteRequest, CreateCreditNoteLineRequest, VoucherTypeDto, SupplyTypeDto, TaxCalculationRequest, PartyDto, TaxFieldContextDto, DocumentTaxLineSnapshotDto } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ADR-0058 §8: a SERVICE row picks a service OR an inventory item (the row turns ITEM).
const LINE_TYPE_OPTIONS: SelectOption[] = [{ value: 'SERVICE', label: 'Service / Item' }, { value: 'REIMBURSEMENT', label: 'Reimbursement' }, { value: 'DISCOUNT', label: 'Discount' }];
/** The combined Service / Item picker's option values: "S:<serviceId>" / "I:<itemId>". */
const SERVICE_PREFIX = 'S:';
const ITEM_PREFIX = 'I:';
const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- Document tax context (ADR-0047 §8) ----
// The jurisdiction's DOCUMENT fields (supply type from the pack, place of supply for a
// state-based regime) render through TaxFieldsPanel; the two bound fields are mirrored into
// typed state so the request carries them both ways. An entity with no jurisdiction, or one
// whose schema declares nothing, falls back to the plain supply-type picker.
const SUPPLY_BINDING = 'DOCUMENT.SUPPLY_TYPE';
const PLACE_BINDING = 'DOCUMENT.PLACE_OF_SUPPLY';
const boundKey = (ctx: TaxFieldContextDto | null, binding: string): string | null =>
  ctx?.fields.find(f => (f.binding || '').toUpperCase() === binding)?.fieldKey ?? null;
/** The values the panel renders and the request sends: extra fields plus the two bound ones. */
const mergeTaxFields = (ctx: TaxFieldContextDto | null, extra: TaxFieldValues, supplyType: string, placeOfSupply: string): TaxFieldValues => {
  const out: TaxFieldValues = { ...extra };
  const sk = boundKey(ctx, SUPPLY_BINDING); if (sk) out[sk] = supplyType;
  const pk = boundKey(ctx, PLACE_BINDING); if (pk) out[pk] = placeOfSupply;
  return out;
};
/** One line of the snapshot breakdown shown under a document line: "CGST 9% 90.00 · SGST 9% 90.00". */
/** The line keys the tax preview refused as "[ITEM_UNMAPPED] Line N": a service with no tax master under the jurisdiction. */
const unmappedLineKeys = (errors?: string[] | null): number[] =>
  (errors || []).map(e => /\[ITEM_UNMAPPED\] Line (\d+)/.exec(e)).filter(Boolean).map(m => Number(m![1]));
const snapshotSummary = (s?: DocumentTaxLineSnapshotDto | null): string | null => {
  if (!s) return null;
  const parts: string[] = [];
  if (s.classificationCode) parts.push(`HSN/SAC ${s.classificationCode}`);
  if (s.taxTreatment && s.taxTreatment !== 'TAXABLE') parts.push(s.taxTreatment === 'ZERO_RATED' ? 'Zero-rated' : 'Exempt');
  else if (s.components.length) parts.push(s.components.map(c => `${c.componentCode} ${c.ratePercentage}% ${money(c.taxAmount)}`).join(' · '));
  if (s.rateCode) parts.push(s.rateCode);
  if (s.isBackfilled) parts.push('(backfilled)');
  return parts.join('  ·  ');
};

// A credit note is editable only while POSTED, unlocked, and not yet applied/consumed.
const editBlockReason = (r: CreditNoteDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return r.lockSource || 'Locked (filed or closed period)';
  if (r.appliedStatus === 'APPLIED') return 'Fully applied';
  if (r.appliedStatus === 'PARTIAL') return 'Partially applied';
  return null;
};
const isEditable = (r: CreditNoteDto): boolean => editBlockReason(r) === null;
const statusBadges = (r: CreditNoteDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.appliedStatus === 'PARTIAL' && <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Partially Applied</span>}
    {r.appliedStatus === 'APPLIED' && <span className="badge" style={{ background: '#16a34a', color: '#fff' }}>Applied</span>}
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }} title={r.lockSource || 'Locked'}>🔒 Locked</span>}
  </span>
);
const formatAddress = (p: PartyDto): string =>
  [p.addressLine1, p.city, p.stateName, p.postalCode, p.countryName].filter(Boolean).join(', ');

/**
 * An entry row. ITEM rows (ADR-0058 §8) carry the item half and the "goods returned" switch
 * (ticked = the goods come back into stock; unticked = a price allowance). A row reversing an
 * invoice item line (`sourceInvoiceLineId`) keeps that line's item and pack, and shows the ledger
 * that line credited (`sourceLedger`). `incomeLedgerId` is used only by a free-text SERVICE row
 * (no service picked) — the ledger it must name; ITEM and service-picked rows follow the masters.
 */
interface LineRow extends ItemFields {
  key: number; lineType: string; serviceId: string; description: string; quantity: string; rate: string; lineAmount: string;
  sourceInvoiceLineId?: string | null; incomeLedgerId: string; goodsReturned: boolean;
  sourceLedger?: { ledgerId?: string | null; ledgerCode?: string | null; ledgerName?: string | null } | null;
}
const blankLine = (key: number): LineRow => ({ key, lineType: 'SERVICE', serviceId: '', description: '', quantity: '', rate: '', lineAmount: '', incomeLedgerId: '', goodsReturned: false, ...blankItemFieldValues() });
/**
 * A line that goes to the server: an amount, or an item with a quantity. A prefilled invoice item
 * line already fully returned (quantity 0, no amount) is left out like an empty service line.
 */
const isSubmitted = (l: LineRow) => parseFloat(l.lineAmount) > 0 || (l.lineType === 'ITEM' && !!l.itemId && parseFloat(l.quantity) > 0);

// ---- Entry modal ----
interface CreditNoteFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit' | 'copy';
  creditNote?: CreditNoteDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, docNo?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}
const CreditNoteFormModal: React.FC<CreditNoteFormModalProps> = ({ isOpen, mode = 'create', creditNote, onSaved, onClose, tabId, moduleName, createInstanceId, copyInstanceId, onRestoreModal, minimizeRef }) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { minimize } = useModalMinimize();
  const isRestoreRef = useRef(false);
  const cnRef = useRef(creditNote); cnRef.current = creditNote;
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId); copyInstanceIdRef.current = copyInstanceId;
  const isCopy = mode === 'copy';
  const [editId, setEditId] = useState<string | null>(null);
  const [editRowVersion, setEditRowVersion] = useState(0);
  const [voucherTypeId, setVoucherTypeId] = useState('');
  const [partyId, setPartyId] = useState('');
  const [againstInvoiceId, setAgainstInvoiceId] = useState('');
  const [creditNoteDate, setCreditNoteDate] = useState(today());
  const [billTo, setBillTo] = useState<PartyDto | null>(null);
  // Role tagging in the picker (party+role): default = customer-tagged parties only (CUSTOMER or
  // BOTH); "search all" widens, and picking a supplier-only party offers a one-click promote to BOTH.
  const [searchAllParties, setSearchAllParties] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [supplyType, setSupplyType] = useState('STANDARD');
  const [placeOfSupplyCode, setPlaceOfSupplyCode] = useState('');
  const [taxFields, setTaxFields] = useState<TaxFieldValues>({});
  const [taxContext, setTaxContext] = useState<TaxFieldContextDto | null>(null);
  const [currencyCode, setCurrencyCode] = useState('');
  const [exchangeRate, setExchangeRate] = useState('1');
  const [functionalCurrency, setFunctionalCurrency] = useState('');
  const [manualNumber, setManualNumber] = useState('');
  const [existingNumber, setExistingNumber] = useState('');
  const [reason, setReason] = useState('');
  const [narration, setNarration] = useState('');
  const [remainingCreditable, setRemainingCreditable] = useState<number | null>(null);
  const [lines, setLines] = useState<LineRow[]>([blankLine(1)]);
  const lineKey = useRef(1);

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeDto[]>([]);
  const [supplyTypes, setSupplyTypes] = useState<SupplyTypeDto[]>([]);
  const partyCache = useRef<Map<string, { label: string; currency?: string | null; partyType?: string }>>(new Map());
  const invoiceCache = useRef<Map<string, string>>(new Map());
  const currencyLabelCache = useRef<Map<string, string>>(new Map());
  const serviceCache = useRef<Map<string, { name: string; rate?: number | null; desc?: string | null }>>(new Map());
  const ledgerCache = useRef<Map<string, string>>(new Map());
  // Item lines (ADR-0058 §8): items not blocked for sale; returns land in any storage location of this BU.
  const itemLookups = useItemLineLookups('SALES_RETURN');
  const loadItemDetailsRef = useRef(itemLookups.loadItemDetails); loadItemDetailsRef.current = itemLookups.loadItemDetails;

  const [tax, setTax] = useState<{ taxable: number; tax: number; grand: number } | null>(null);
  /** Why tax is absent, when it is. Null when tax calculated normally (including a genuine zero). */
  const [taxNote, setTaxNote] = useState<string | null>(null);
  const [taxByKey, setTaxByKey] = useState<Record<number, { tax: number; total: number }>>({});
  // Lines whose service has no tax master: flagged on the line and Save is refused (the post
  // would refuse them too). The default master is a pre-fill, so this is the real guard.
  const [unmappedKeys, setUnmappedKeys] = useState<number[]>([]);
  const [descFocusKey, setDescFocusKey] = useState<number | null>(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // A save-time note that the place of supply differs from the customer's (flagged, not blocked).
  const [placeWarning, setPlaceWarning] = useState<string | null>(null);
  // The place the customer last produced. While the place still equals it, a customer change re-derives it;
  // a place the user changed is kept.
  const customerPlaceRef = useRef<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const initialSnapshotRef = useRef<string>('');

  const loadLookups = useCallback(async () => {
    try {
      const [vt, st, ctx] = await Promise.all([
        voucherTypeService.getAll({ voucherClass: 'SALES_CREDIT_NOTE', pageSize: 100 }),
        taxMetaService.getSupplyTypes(),
        taxMetaService.getFieldContext('DOCUMENT').catch(() => null),
      ]);
      setVoucherTypes(vt.data);
      setSupplyTypes(st);
      setTaxContext(ctx);
    } catch (err: any) {
      setVoucherTypes([]); setSupplyTypes([]);
      notificationService.toast.error(err?.response?.data?.message || 'Could not load voucher types / supply types.');
    }
  }, []);
  useEffect(() => { if (isOpen) loadLookups(); }, [isOpen, loadLookups]);

  const rowsFromCn = (cn: CreditNoteDto): LineRow[] => (cn.lines || []).map((l: CreditNoteLineDto, idx) => {
    if (l.serviceId) serviceCache.current.set(l.serviceId, { name: l.serviceName || '', rate: l.rate, desc: l.description });
    const isItem = l.lineType === 'ITEM';
    const isFreeText = l.lineType === 'SERVICE' && !l.serviceId;
    if (isFreeText && l.incomeLedgerId) ledgerCache.current.set(l.incomeLedgerId, `${l.incomeLedgerCode || ''} - ${l.incomeLedgerName || ''}`);
    return {
      key: idx + 1, lineType: l.lineType, serviceId: l.serviceId || '', description: l.description || '', quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '', lineAmount: String(l.lineAmount), sourceInvoiceLineId: l.sourceInvoiceLineId,
      incomeLedgerId: isFreeText ? (l.incomeLedgerId || '') : '',
      // A saved reversal credited the invoice line's ledger — show it as such.
      sourceLedger: isItem && l.sourceInvoiceLineId && l.incomeLedgerId
        ? { ledgerId: l.incomeLedgerId, ledgerCode: l.incomeLedgerCode, ledgerName: l.incomeLedgerName } : null,
      goodsReturned: isItem ? (l.goodsReturned ?? !!l.stockLocationId) : false,
      ...(isItem ? itemFieldValuesOf(l) : blankItemFieldValues()),
    };
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    lineKey.current = 1;
    setTax(null); setTaxByKey({}); setErrors({}); setHeaderCollapsed(false); setMaximized(false); setRemainingCreditable(null); setSearchAllParties(false);

    const cn = cnRef.current;
    if ((modeRef.current === 'edit' || modeRef.current === 'copy') && cn) {
      // Packs + item types for the note's item lines (best-effort).
      void loadItemDetailsRef.current((cn.lines || []).filter(l => l.lineType === 'ITEM' && l.itemId).map(l => l.itemId!));
      const rows = rowsFromCn(cn);
      lineKey.current = Math.max(1, rows.length);
      const linesToSet = rows.length ? rows : [blankLine(1)];
      const editing = modeRef.current === 'edit';
      setEditId(editing ? cn.id : null); setEditRowVersion(editing ? cn.rowVersion : 0);
      setVoucherTypeId(cn.voucherTypeId || '');
      setPartyId(cn.partyId); partyCache.current.set(cn.partyId, { label: cn.partyName || '', currency: cn.currencyCode });
      setAgainstInvoiceId(editing ? (cn.againstInvoiceId || '') : (cn.againstInvoiceId || ''));
      if (cn.againstInvoiceId && cn.againstInvoiceNumber) invoiceCache.current.set(cn.againstInvoiceId, cn.againstInvoiceNumber);
      setCreditNoteDate(editing ? (cn.creditNoteDate || '').slice(0, 10) : docToday());
      setSupplyType(cn.supplyType || 'STANDARD');
      setPlaceOfSupplyCode(cn.placeOfSupplyCode || ''); setTaxFields({ ...(cn.taxAttributes || {}) });
      setCurrencyCode(cn.currencyCode || ''); setExchangeRate(String(cn.exchangeRate ?? 1));
      setExistingNumber(editing ? (cn.creditNoteNumber || '') : ''); setManualNumber(editing ? (cn.creditNoteNumber || '') : '');
      setReason(cn.reason || ''); setNarration(cn.narration || '');
      setLines(linesToSet);
      initialSnapshotRef.current = JSON.stringify({
        voucherTypeId: cn.voucherTypeId || '', partyId: cn.partyId, againstInvoiceId: cn.againstInvoiceId || '',
        creditNoteDate: editing ? (cn.creditNoteDate || '').slice(0, 10) : docToday(), supplyType: cn.supplyType || 'STANDARD',
        placeOfSupplyCode: cn.placeOfSupplyCode || '', taxFields: { ...(cn.taxAttributes || {}) },
        currencyCode: cn.currencyCode || '', exchangeRate: String(cn.exchangeRate ?? 1), manualNumber: editing ? (cn.creditNoteNumber || '') : '', reason: cn.reason || '', narration: cn.narration || '', lines: linesToSet,
      });
      entityAccountingConfigService.getConfig().then(c => setFunctionalCurrency(c.functionalCurrencyCode || '')).catch(() => {});
      partyService.getById(cn.partyId).then(setBillTo).catch(() => setBillTo(null));
    } else {
      const createLines = [blankLine(1)];
      setEditId(null); setEditRowVersion(0);
      setVoucherTypeId(''); setPartyId(''); setAgainstInvoiceId(''); setCreditNoteDate(docToday()); setBillTo(null); setSupplyType('STANDARD');
      setPlaceOfSupplyCode(''); setTaxFields({});
      setCurrencyCode(''); setExchangeRate('1'); setExistingNumber(''); setManualNumber(''); setReason(''); setNarration(''); setLines(createLines);
      initialSnapshotRef.current = JSON.stringify({
        voucherTypeId: '', partyId: '', againstInvoiceId: '', creditNoteDate: docToday(), supplyType: 'STANDARD', placeOfSupplyCode: '', taxFields: {},
        currencyCode: '', exchangeRate: '1', manualNumber: '', reason: '', narration: '', lines: createLines,
      });
      entityAccountingConfigService.getConfig().then(c => {
        const fc = c.functionalCurrencyCode || '';
        setFunctionalCurrency(fc);
        setCurrencyCode(prev => {
          const next = prev || fc;
          try { const snap = JSON.parse(initialSnapshotRef.current); snap.currencyCode = next; initialSnapshotRef.current = JSON.stringify(snap); } catch { /* */ }
          return next;
        });
      }).catch(() => {});
    }
  }, [isOpen]);

  const selectedVt = voucherTypes.find(v => v.id === voucherTypeId);
  const supplyTypeOptions: SelectOption[] = supplyTypes.map(s => ({ value: s.supplyCode, label: s.supplyName }));

  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await partyService.getAll({
        pageSize: 50, searchTerm: input || undefined,
        // Customer-tagged by default (CUSTOMER or BOTH — the server widens BOTH itself).
        partyType: searchAllParties ? undefined : 'CUSTOMER',
      });
      r.data.forEach(p => partyCache.current.set(p.id, { label: `${p.partyCode} - ${p.partyName}`, currency: p.currencyCode, partyType: p.partyType }));
      return r.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}${p.partyType === 'SUPPLIER' ? '  (supplier)' : ''}` }));
    } catch { return []; }
  }, [searchAllParties]);
  /** Tag a supplier as customer too (party_type → BOTH) so it lands in the customer picker for good. */
  const promoteToCustomer = async () => {
    if (!billTo) return;
    setPromoting(true);
    try {
      await partyService.update(billTo.id, {
        partyCode: billTo.partyCode, partyName: billTo.partyName, legalName: billTo.legalName,
        partyType: 'BOTH', partyGroupId: billTo.partyGroupId,
        email: billTo.email, phone: billTo.phone,
        countryId: billTo.countryId, countryName: billTo.countryName, stateName: billTo.stateName,
        addressLine1: billTo.addressLine1, city: billTo.city, postalCode: billTo.postalCode,
        currencyCode: billTo.currencyCode, creditDays: billTo.creditDays, notes: billTo.notes,
        statusCode: billTo.statusCode || 'ACTIVE',
        rowVersion: billTo.rowVersion,
      });
      const refreshed = await partyService.getById(billTo.id);
      setBillTo(refreshed);
      partyCache.current.set(refreshed.id, { label: `${refreshed.partyCode} - ${refreshed.partyName}`, currency: refreshed.currencyCode, partyType: refreshed.partyType });
      notificationService.toast.success(`${refreshed.partyName} is now tagged as a customer.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not tag the party as a customer.');
    } finally { setPromoting(false); }
  };
  const onPickParty = (id: string) => {
    setPartyId(id);
    // Changing party invalidates any selected invoice.
    setAgainstInvoiceId(''); setRemainingCreditable(null);
    const cur = partyCache.current.get(id)?.currency || functionalCurrency;
    setCurrencyCode(cur);
    // A state-based jurisdiction defaults the place of supply to the buyer's state (ADR-0047 §8,
    // Slice 4); the document may still override it. Only when the schema declares the field and
    // the user has not chosen one.
    // A buyer with no state on record takes the supplier's place — the issuing registration's
    // state, i.e. this business unit's (the general rule for a recipient with no location).
    if (id && taxContext && boundKey(taxContext, PLACE_BINDING) && (!placeOfSupplyCode || placeOfSupplyCode === customerPlaceRef.current)) {
      const supplier = taxContext.supplierStateCode || '';
      taxProfileService.getPartyProfile(id)
        .then(p => { const place = p?.taxStateCode || supplier; customerPlaceRef.current = place; setPlaceOfSupplyCode(place); })
        .catch(() => { customerPlaceRef.current = supplier; setPlaceOfSupplyCode(supplier); });
    }
    if (cur === functionalCurrency) setExchangeRate('1');
    if (!id) { setBillTo(null); return; }
    partyService.getById(id).then(setBillTo).catch(() => setBillTo(null));
  };

  const loadInvoices = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const r = await salesInvoiceService.getAll({ partyId: partyId || undefined, searchTerm: input || undefined, pageSize: 50 });
      r.data.forEach(inv => invoiceCache.current.set(inv.id, `${inv.invoiceNumber || inv.id} — ${inv.currencyCode || ''} ${money(inv.grandTotal)}`));
      return r.data.map(inv => ({ value: inv.id, label: `${inv.invoiceNumber || '—'} — ${inv.partyName || ''} — ${inv.currencyCode || ''} ${money(inv.grandTotal)}` }));
    } catch { return []; }
  }, [partyId]);

  const loadCurrencies = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      return list.map(c => {
        const label = `${c.currencyCode} — ${c.currencyName}`;
        currencyLabelCache.current.set(c.currencyCode, label);
        return { value: c.currencyCode, label };
      });
    } catch {
      return [];
    }
  }, []);

  // A linked note reverses an invoice, so the server takes that invoice's currency and rate and
  // ignores the request's. The form must not offer a choice it would not honour.
  const isLinkedToInvoice = !!againstInvoiceId;

  /** Changing currency resets the rate — one carried over from another currency means nothing. */
  const onPickCurrency = (code: string) => {
    setCurrencyCode(code);
    setExchangeRate(code === functionalCurrency ? '1' : '');
  };

  // Pick an invoice → prefill its lines + party/supply/currency + remaining creditable cap.
  const onPickInvoice = (invId: string) => {
    setAgainstInvoiceId(invId);
    if (!invId) { setRemainingCreditable(null); return; }
    // The invoice itself names each item line's item, pack and location (the prefill carries ids only).
    Promise.all([creditNoteService.getInvoicePrefill(invId), salesInvoiceService.getById(invId).catch(() => null)]).then(([pf, inv]) => {
      const invLines = new Map<string, SalesInvoiceLineDto>((inv?.lines || []).map(il => [il.id, il]));
      if (!partyId) { setPartyId(pf.partyId); partyCache.current.set(pf.partyId, { label: pf.partyName || '', currency: pf.currencyCode }); partyService.getById(pf.partyId).then(setBillTo).catch(() => {}); }
      setSupplyType(pf.supplyType || 'STANDARD');
      setPlaceOfSupplyCode(pf.placeOfSupplyCode || ''); setTaxFields({ ...(pf.taxFields || {}) });
      if (pf.currencyCode) { setCurrencyCode(pf.currencyCode); setExchangeRate(String(pf.exchangeRate ?? 1)); }
      setRemainingCreditable(pf.remainingCreditable);
      lineKey.current = Math.max(1, pf.lines.length);
      const rows: LineRow[] = pf.lines.map((l, idx) => {
        const isItem = l.lineType === 'ITEM';
        const src = l.sourceInvoiceLineId ? invLines.get(l.sourceInvoiceLineId) : undefined;
        const isFreeText = l.lineType === 'SERVICE' && !l.serviceId;
        if (isFreeText && l.incomeLedgerId && src?.incomeLedgerId === l.incomeLedgerId) {
          ledgerCache.current.set(l.incomeLedgerId, `${src.incomeLedgerCode || ''} - ${src.incomeLedgerName || ''}`);
        }
        // Reversing an invoice item line credits the ledger that line credited — shown read-only.
        const srcLedgerId = l.incomeLedgerId || src?.incomeLedgerId;
        const sourceLedger = isItem && l.sourceInvoiceLineId && srcLedgerId
          ? { ledgerId: srcLedgerId, ledgerCode: src?.incomeLedgerCode, ledgerName: src?.incomeLedgerName } : null;
        // An item line offers the quantity not yet returned; its amount follows (qty × rate) when that differs.
        const partly = isItem && l.quantity != null && l.rate != null && src?.quantity != null && l.quantity !== src.quantity;
        return {
          key: idx + 1, lineType: l.lineType, serviceId: l.serviceId || '', description: l.description || '', quantity: l.quantity != null ? String(l.quantity) : '', rate: l.rate != null ? String(l.rate) : '',
          lineAmount: partly ? (l.quantity! * l.rate!).toFixed(2) : String(l.lineAmount), sourceInvoiceLineId: l.sourceInvoiceLineId,
          // Only a free-text service line names its own ledger (the invoice line's, to start with).
          incomeLedgerId: isFreeText ? (l.incomeLedgerId || '') : '',
          sourceLedger,
          goodsReturned: isItem ? (l.goodsReturned ?? true) : false,
          ...(isItem
            ? itemFieldValuesOf({
                itemId: l.itemId ?? src?.itemId, itemCode: src?.itemCode, itemName: src?.itemName,
                uomId: l.uomId ?? src?.uomId, uomCode: src?.uomCode, factorToBase: src?.factorToBase,
                stockLocationId: l.stockLocationId ?? src?.stockLocationId,
                locationCode: src?.locationCode, locationName: src?.locationName,
              })
            : blankItemFieldValues()),
        };
      });
      void loadItemDetailsRef.current(rows.filter(r => r.lineType === 'ITEM' && r.itemId).map(r => r.itemId));
      setLines(rows.length ? rows : [blankLine(1)]);
      // Debit notes raised on the invoice add to what may be credited (ADR-0053).
      const raised = pf.debitNoteTotal ? ` (incl. debit notes of ${money(pf.debitNoteTotal)})` : '';
      notificationService.toast.info(`Prefilled from invoice ${pf.invoiceNumber || ''}. Remaining creditable: ${pf.currencyCode || ''} ${money(pf.remainingCreditable)}${raised}.`);
    }).catch(() => notificationService.toast.error('Could not prefill from the invoice.'));
  };

  const loadServices = useCallback(async (input: string, itemType: string): Promise<SelectOption[]> => {
    try {
      const r = await serviceService.getAll({ itemType, pageSize: 50, searchTerm: input || undefined });
      r.data.forEach(s => serviceCache.current.set(s.id, { name: `${s.serviceCode} - ${s.serviceName}`, rate: s.defaultRate, desc: s.description }));
      return r.data.map(s => ({ value: s.id, label: `${s.serviceCode} - ${s.serviceName}` }));
    } catch { return []; }
  }, []);

  const updLine = (key: number, patch: Partial<LineRow>) => setLines(rows => rows.map(r => {
    if (r.key !== key) return r;
    const n = { ...r, ...patch };
    if (patch.quantity !== undefined || patch.rate !== undefined) {
      const q = parseFloat(n.quantity), rt = parseFloat(n.rate);
      if (!isNaN(q) && !isNaN(rt)) n.lineAmount = (q * rt).toFixed(2);
    }
    return n;
  }));
  const onPickService = (key: number, sid: string) => {
    const meta = serviceCache.current.get(sid);
    // A picked service's ledger follows the Service Master — any free-text ledger pick goes.
    updLine(key, { serviceId: sid, incomeLedgerId: '', description: meta?.desc || meta?.name || '', rate: meta?.rate != null ? String(meta.rate) : '' });
  };
  /** The Service / Item picker: services (as today) and items not blocked for sale, tagged. */
  const { loadItems } = itemLookups;
  const loadServicesAndItems = useCallback(async (input: string): Promise<SelectOption[]> => {
    const [services, items] = await Promise.all([loadServices(input, 'SERVICE'), loadItems(input)]);
    return [
      ...services.map(o => ({ value: `${SERVICE_PREFIX}${o.value}`, label: `Service · ${o.label}` })),
      ...items.map(o => ({ value: `${ITEM_PREFIX}${o.value}`, label: `Item · ${o.label}` })),
    ];
  }, [loadServices, loadItems]);
  /**
   * A pick from the Service / Item picker. A service keeps today's behaviour (SERVICE row); an
   * item turns the row ITEM (goods returned by default), clears the service and its price, and
   * defaults the description to the item's name once the item's read lands.
   */
  const onPickProduct = (row: LineRow, value: string) => {
    if (value.startsWith(ITEM_PREFIX)) {
      const itemId = value.slice(ITEM_PREFIX.length);
      if (row.lineType === 'ITEM' && row.itemId === itemId) return;
      updLine(row.key, { lineType: 'ITEM', serviceId: '', incomeLedgerId: '', goodsReturned: true, description: '', rate: '', lineAmount: '' });
      void itemLookups.pickItem(row.key, itemId, setLines).then(() => setLines(rows => rows.map(r =>
        r.key === row.key && r.itemId === itemId && !r.description.trim() ? { ...r, description: r.itemName } : r)));
      return;
    }
    const sid = value.startsWith(SERVICE_PREFIX) ? value.slice(SERVICE_PREFIX.length) : '';
    if (row.lineType === 'ITEM') updLine(row.key, { lineType: 'SERVICE', incomeLedgerId: '', goodsReturned: false, ...blankItemFieldValues() });
    onPickService(row.key, sid);
  };
  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => ledgerCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch { return []; }
  }, []);
  const addLine = () => setLines(rows => [...rows, blankLine(++lineKey.current)]);
  const removeLine = (key: number) => setLines(rows => rows.length > 1 ? rows.filter(r => r.key !== key) : rows);

  // Item lines count with services (the server's subtotal: SERVICE + ITEM).
  const subtotal = lines.filter(l => l.lineType === 'SERVICE' || l.lineType === 'ITEM').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const hasItemLines = lines.some(l => l.lineType === 'ITEM');
  const reimbursementTotal = lines.filter(l => l.lineType === 'REIMBURSEMENT').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const discountTotal = lines.filter(l => l.lineType === 'DISCOUNT').reduce((s, l) => s + (parseFloat(l.lineAmount) || 0), 0);
  const taxableNet = subtotal + reimbursementTotal - discountTotal;

  // ---- Income ledger: read-only on ITEM and service-picked lines (ADR-0058 §8) ----
  // A line reversing an invoice item line shows that line's ledger, so it is not read.
  const lineLedgers = useSalesLineLedgers('CREDIT_NOTE', creditNoteDate, lines.filter(l => l.lineType === 'ITEM' && !l.sourceLedger?.ledgerId).map(l => l.itemId), isOpen);
  const serviceLedgers = useSalesServiceLedgers('CREDIT_NOTE', lines.filter(l => l.lineType === 'SERVICE').map(l => l.serviceId), isOpen);
  /**
   * The income ledger an ITEM line or a service-picked SERVICE line credits back (undefined = not
   * read yet; null = nothing resolved). Other lines: undefined.
   */
  const resolvedLedgerOf = (l: LineRow): ResolvedLineLedger => {
    if (l.lineType === 'ITEM' && l.itemId) {
      if (l.sourceLedger?.ledgerId) return { ...l.sourceLedger, tag: 'as on the invoice' };
      const info = lineLedgers[l.itemId];
      if (!info) return undefined;
      return info.ledgerId ? { ledgerId: info.ledgerId, ledgerCode: info.ledgerCode, ledgerName: info.ledgerName, tag: ledgerSourceTag(info) } : null;
    }
    if (l.lineType === 'SERVICE' && l.serviceId) {
      const info = serviceLedgers[l.serviceId];
      if (!info) return undefined;
      return info.ledgerId ? { ledgerId: info.ledgerId, ledgerCode: info.ledgerCode, ledgerName: info.ledgerName, tag: serviceLedgerSourceTag(info.source) } : null;
    }
    return undefined;
  };
  /** A SERVICE line with no service picked — it names its own income ledger. */
  const isFreeTextService = (l: LineRow) => l.lineType === 'SERVICE' && !l.serviceId;
  /** Whether an ITEM row brings goods back into stock: ticked, and not a non-stock item. */
  const movesStock = (l: LineRow) => l.goodsReturned && itemLookups.itemTypeOf(l) !== 'NON_STOCK';

  const taxKey = JSON.stringify({ supplyType, placeOfSupplyCode, taxFields, creditNoteDate, lines: lines.map(l => ({ t: l.lineType, s: l.serviceId, i: l.itemId, a: l.lineAmount, q: l.quantity })) });
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(async () => {
      const reqLines = lines.filter(l => parseFloat(l.lineAmount) > 0).map(l => ({
        lineId: String(l.key),
        // An ITEM line is priced from the item's tax mapping, as a SERVICE line is from the service's.
        ...(l.lineType === 'ITEM' ? { serviceId: null, itemId: l.itemId || null } : { serviceId: l.serviceId || null }),
        taxableAmount: l.lineType === 'DISCOUNT' ? -(parseFloat(l.lineAmount) || 0) : (parseFloat(l.lineAmount) || 0),
        quantity: l.quantity ? parseFloat(l.quantity) : null,
        isExempt: l.lineType !== 'DISCOUNT' && !(l.lineType === 'ITEM' ? l.itemId : l.serviceId) ? true : undefined,
      }));
      if (reqLines.length === 0) { setTax(null); setTaxByKey({}); setUnmappedKeys([]); return; }
      try {
        // Preview note: a line credited against an invoice line is priced from that invoice's
        // snapshot on post; the preview shows the live rate, which is the same unless the
        // master changed since the invoice.
        const req: TaxCalculationRequest = {
          transactionDate: creditNoteDate, supplyType, direction: 'OUTPUT', lines: reqLines,
          placeOfSupplyStateCode: placeOfSupplyCode || null,
          taxFields: mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode),
        };
        const res = await taxMetaService.calculate(req);
        // A failed calculation returns a zeroed summary and no lines, plus the reason. Reading
        // that as an answer showed a Grand Total of 0 on a document whose lines were not zero.
        if (res.success === false) {
          setTax(null); setTaxByKey({});
          setTaxNote(res.errors?.[0] || 'Tax could not be calculated for this entity.');
          setUnmappedKeys(unmappedLineKeys(res.errors));
          return;
        }
        setTaxNote(null); setUnmappedKeys([]);
        setTax({ taxable: res.summary.totalTaxableAmount, tax: res.summary.totalTaxAmount, grand: res.summary.grandTotal });
        const map: Record<number, { tax: number; total: number }> = {};
        (res.lines || []).forEach(ln => { map[Number(ln.lineId)] = { tax: ln.totalTaxAmount, total: ln.lineTotal }; });
        setTaxByKey(map);
      } catch {
        setTax(null); setTaxByKey({});
        setTaxNote('Tax could not be calculated — the total below excludes tax.');
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxKey, isOpen]);

  // Same derivation the server uses on post, so the screen and the posted document agree even
  // when tax is unavailable (see SalesInvoiceScreen for the case that exposed this).
  const taxTotal = tax ? tax.tax : 0;
  const grandTotal = taxableNet + taxTotal;
  const overCredit = remainingCreditable != null && grandTotal > remainingCreditable + 0.005;

  const currentSnapshot = useMemo(() => JSON.stringify({
    voucherTypeId, partyId, againstInvoiceId, creditNoteDate, supplyType, currencyCode, exchangeRate, manualNumber, reason, narration, lines,
  }), [voucherTypeId, partyId, againstInvoiceId, creditNoteDate, supplyType, currencyCode, exchangeRate, manualNumber, reason, narration, lines]);
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return currentSnapshot !== initialSnapshotRef.current;
  }, [currentSnapshot]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleSave = async () => {
    const e: Record<string, string> = {};
    if (!voucherTypeId) e.voucherTypeId = 'Voucher type is required';
    if (!partyId) e.partyId = 'Customer is required';
    if (selectedVt?.numberingMethod === 'MANUAL' && !manualNumber.trim()) e.documentNumber = 'Number is required';
    const valid = lines.filter(isSubmitted);
    if (valid.length === 0) e.lines = 'Add at least one line with an amount';
    if (valid.some(l => l.lineType === 'SERVICE' && !l.serviceId && !l.description.trim())) e.lines = 'Service lines need a service or description';
    if (unmappedKeys.some(k => valid.some(l => l.key === k))) e.lines = 'A service or item on this document has no tax master. Map it (Service Master / the item\'s tax mapping) or pick another before posting.';
    if (overCredit) e.lines = `Credit exceeds the invoice's remaining creditable amount (${currencyCode} ${money(remainingCreditable || 0)}).`;
    // ITEM lines (ADR-0058 §8) — the server's rules, caught early (return ceilings stay server-side).
    const lineErrors: string[] = [];
    valid.forEach((l, idx) => {
      if (l.lineType !== 'ITEM') return;
      const n = idx + 1;
      if (!l.itemId) { e[`line-${l.key}-item`] = 'Required'; lineErrors.push(`Line ${n}: pick the item`); return; }
      if (!(parseFloat(l.quantity) > 0)) { e[`line-${l.key}-quantity`] = 'Required'; lineErrors.push(`Line ${n}: enter the quantity (more than 0)`); }
      if (movesStock(l) && itemLookups.itemTypeOf(l) === 'STOCK' && !l.stockLocationId) { e[`line-${l.key}-location`] = 'Required'; lineErrors.push(`Line ${n}: pick the location the goods come back to`); }
    });
    if (lineErrors.length && !e.lines) e.lines = lineErrors[0];
    // Income ledger: ITEM and service-picked lines follow the masters (a reversal, the invoice
    // line's) — nothing resolved blocks the post; a free-text SERVICE line must name its own.
    valid.forEach(l => {
      if (isFreeTextService(l)) {
        if (!l.incomeLedgerId) { e[`line-${l.key}-ledger`] = 'Required'; if (!e.lines) e.lines = 'A service line without a service needs its income ledger'; }
        return;
      }
      if (resolvedLedgerOf(l) === null) {
        e[`line-${l.key}-ledger`] = 'Required';
        if (!e.lines) e.lines = l.lineType === 'ITEM' ? ITEM_INCOME_LEDGER_MISSING : SERVICE_INCOME_LEDGER_MISSING;
      }
    });
    Object.entries(validateTaxFields(taxContext, mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode)))
      .forEach(([k, v]) => { e[`tax.${k}`] = v; });
    // Per-field line flags (line-<key>-…) only highlight; the toast reads a sentence.
    if (Object.keys(e).length) { setErrors(e); notificationService.toast.warning(Object.entries(e).find(([k]) => !k.startsWith('line-'))?.[1] || Object.values(e)[0]); return; }
    // The place of supply should be the customer's. A difference is shown in the confirmation; the save is not blocked.
    const customerPlace = taxContext && partyId && boundKey(taxContext, PLACE_BINDING)
      ? await taxProfileService.getPartyProfile(partyId).then(p => p?.taxStateCode || '').catch(() => '')
      : '';
    setPlaceWarning(customerPlace && placeOfSupplyCode && customerPlace !== placeOfSupplyCode
      ? `The place of supply (${placeOfSupplyCode}) differs from the customer's place of supply (${customerPlace}).`
      : null);
    setErrors({}); setConfirmOpen(true);
  };
  const handleConfirmSave = async () => {
    setConfirmOpen(false); setSaving(true);
    try {
      const payload: CreateCreditNoteRequest = {
        voucherTypeId, partyId, againstInvoiceId: againstInvoiceId || undefined, creditNoteDate, supplyType,
        placeOfSupplyCode: placeOfSupplyCode || null,
        taxFields: mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode),
        currencyCode, exchangeRate: parseFloat(exchangeRate) || 1,
        manualNumber: (selectedVt?.numberingMethod === 'MANUAL' && manualNumber.trim()) ? manualNumber.trim() : undefined,
        reason: reason.trim() || undefined, narration: narration.trim() || undefined,
        lines: lines.filter(isSubmitted).map((l): CreateCreditNoteLineRequest => {
          if (l.lineType === 'ITEM') {
            const moves = movesStock(l);
            return {
              lineType: 'ITEM', itemId: l.itemId, uomId: l.uomId || undefined,
              // Unticked = a price allowance: no stock, no location.
              goodsReturned: moves, stockLocationId: moves ? (l.stockLocationId || undefined) : undefined,
              // Blank description → the item's name.
              description: l.description.trim() || l.itemName || undefined,
              quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
              lineAmount: parseFloat(l.lineAmount) || 0, sourceInvoiceLineId: l.sourceInvoiceLineId || undefined,
              // No incomeLedgerId — an ITEM line's ledger follows the masters / the invoice line (the server ignores one).
            };
          }
          return {
            lineType: l.lineType, serviceId: l.serviceId || undefined, description: l.description.trim() || undefined,
            quantity: l.quantity ? parseFloat(l.quantity) : undefined, rate: l.rate ? parseFloat(l.rate) : undefined,
            lineAmount: parseFloat(l.lineAmount) || 0, sourceInvoiceLineId: l.sourceInvoiceLineId || undefined,
            // A free-text SERVICE line names its own ledger; a service-picked one follows the Service Master.
            ...(isFreeTextService(l) ? { incomeLedgerId: l.incomeLedgerId || undefined } : {}),
          };
        }),
      };
      const saved = editId
        ? await creditNoteService.update(editId, { ...payload, rowVersion: editRowVersion })
        : await creditNoteService.create(payload);
      setSaving(false);
      loadLookups();
      notificationService.toast.success(`Credit Note ${saved.creditNoteNumber || ''} ${editId ? 'updated' : 'posted'}.`);
      // Negative stock under the WARN policy posts, but says so (BLOCK refuses the save instead).
      if (saved.stockWarnings?.length) notificationService.toast.warning(`Stock goes below zero — ${saved.stockWarnings.join(' · ')}`);
      onSaved(); onClose();
    } catch (err: any) { setSaving(false); notificationService.toast.error(err?.response?.data?.message || `Could not ${editId ? 'update' : 'post'} the credit note.`); }
  };

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const editing = currentMode === 'edit';
    const copying = currentMode === 'copy';
    const cId = createInstanceIdRef.current || '1';
    const cpId = copyInstanceIdRef.current || '1';
    const sourceNo = cnRef.current?.creditNoteNumber;
    const snap = { editId, editRowVersion, voucherTypeId, partyId, againstInvoiceId, creditNoteDate, billTo, supplyType, placeOfSupplyCode, taxFields: { ...taxFields }, currencyCode, exchangeRate, functionalCurrency, manualNumber, existingNumber, reason, narration, remainingCreditable, lines: lines.map(l => ({ ...l })) };
    const savedInitialSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedInitialSnapshot ? currentSnapshot !== savedInitialSnapshot : false;
    const modalId = editing ? `${tabId}-form-modal-edit-${existingNumber || editId || ''}` : copying ? `${tabId}-form-modal-copy-${cpId}` : `${tabId}-form-modal-create-${cId}`;
    const title = editing ? `Edit Credit Note - ${existingNumber || ''}` : copying ? `Copy Credit Note - ${sourceNo || 'draft'} (${cpId})` : partyId ? `Credit Note - ${partyCache.current.get(partyId)?.label || ''} (${cId})` : `New Credit Note (${cId})`;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setEditId(snap.editId); setEditRowVersion(snap.editRowVersion);
        setVoucherTypeId(snap.voucherTypeId); setPartyId(snap.partyId); setAgainstInvoiceId(snap.againstInvoiceId); setCreditNoteDate(snap.creditNoteDate); setBillTo(snap.billTo);
        setSupplyType(snap.supplyType); setPlaceOfSupplyCode(snap.placeOfSupplyCode); setTaxFields(snap.taxFields); setCurrencyCode(snap.currencyCode); setExchangeRate(snap.exchangeRate); setFunctionalCurrency(snap.functionalCurrency);
        setManualNumber(snap.manualNumber); setExistingNumber(snap.existingNumber); setReason(snap.reason); setNarration(snap.narration); setRemainingCreditable(snap.remainingCreditable); setLines(snap.lines);
        initialSnapshotRef.current = savedInitialSnapshot;
        loadLookups();
        onRestoreModal?.(editing ? 'edit' : copying ? 'copy' : 'create', editing ? undefined : copying ? cpId : cId, editing ? (snap.existingNumber || undefined) : copying ? (sourceNo || undefined) : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, editId, editRowVersion, voucherTypeId, partyId, againstInvoiceId, creditNoteDate, billTo, supplyType, placeOfSupplyCode, taxFields, currencyCode, exchangeRate, functionalCurrency, manualNumber, existingNumber, reason, narration, remainingCreditable, lines, currentSnapshot, loadLookups]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className={`modal-container ${maximized ? '' : 'modal-xl'}`} style={maximized ? { width: '98vw', maxWidth: '98vw', height: '96vh', maxHeight: '96vh' } : undefined}>
        {saving && <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}><div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>}
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">{editId ? `Edit Credit Note ${existingNumber || ''}` : isCopy ? `Copy Credit Note${creditNote ? ` — from ${creditNote.creditNoteNumber}` : ''}` : 'New Credit Note'}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}><button className="modal-header-btn" onClick={() => setMaximized(m => !m)} title={maximized ? 'Restore' : 'Maximize'} disabled={saving}>{maximized ? <IconSystem.windowRestore size={16} /> : <IconSystem.maximize size={16} />}</button>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          <div onClick={() => setHeaderCollapsed(c => !c)} role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHeaderCollapsed(c => !c); } }}
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.15rem 0', marginBottom: headerCollapsed ? '0.4rem' : '0.5rem' }}>
            <span className="form-label" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <IconSystem.chevronDown size={14} style={{ transform: headerCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.12s' }} />
              Credit Note Details
            </span>
            {headerCollapsed && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(partyId && partyCache.current.get(partyId)?.label) || 'No customer'} · {creditNoteDate}{currencyCode ? ` · ${currencyCode}` : ''}{againstInvoiceId ? ` · vs ${invoiceCache.current.get(againstInvoiceId) || 'invoice'}` : ' · on account'}
            </span>}
          </div>
          {!headerCollapsed && (<>
          <div className="form-grid-3">
            <div className="form-group"><SyutSelect label={<>Voucher Type <span className="text-danger">*</span></>} options={voucherTypes.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` }))} value={voucherTypeId} onChange={v => setVoucherTypeId((v as string) || '')} isClearable={false} isDisabled={!!editId} menuPortalTarget={document.body} error={errors.voucherTypeId} /></div>
            <div className="form-group">
              <SyutSelect key={String(searchAllParties)} label={<>Customer <span className="text-danger">*</span></>} isAsync loadOptions={loadParties} defaultOptions cacheOptions={false}
                seedOptions={partyId ? [{ value: partyId, label: partyCache.current.get(partyId)?.label || '' }] : undefined}
                menuPortalTarget={document.body} value={partyId} onChange={v => onPickParty((v as string) || '')} placeholder={searchAllParties ? 'Search all parties' : 'Select customer'} error={errors.partyId} />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={searchAllParties} onChange={e => setSearchAllParties(e.target.checked)} />
                Customer not listed? Search all parties
              </label>
            </div>
            <div className="form-group"><SyutSelect label={<>Against Invoice <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></>} isAsync key={partyId} loadOptions={loadInvoices} defaultOptions cacheOptions={false}
              seedOptions={againstInvoiceId ? [{ value: againstInvoiceId, label: invoiceCache.current.get(againstInvoiceId) || 'invoice' }] : undefined}
              menuPortalTarget={document.body} value={againstInvoiceId} onChange={v => onPickInvoice((v as string) || '')} isClearable placeholder="On account (no invoice)" /></div>
            <div className="form-group"><DocumentNumberField label="Credit Note No." voucherTypeId={voucherTypeId} date={creditNoteDate} existingNumber={editId ? existingNumber : ''} manualValue={manualNumber} onManualChange={setManualNumber} required error={errors.documentNumber} editableExisting={selectedVt?.numberingMethod === 'MANUAL'} /></div>
            <div className="form-group"><label className="form-label">Credit Note Date <span className="text-danger">*</span></label><FyDateInput className="form-input" value={creditNoteDate} onChange={setCreditNoteDate} /></div>
            {taxContext?.fields?.length ? (
              <TaxFieldsPanel context={taxContext} bare
                values={mergeTaxFields(taxContext, taxFields, supplyType, placeOfSupplyCode)}
                errors={Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('tax.')).map(([k, v]) => [k.slice(4), v]))}
                onChange={(key, value) => {
                  setErrors(prev => { const n = { ...prev }; delete n[`tax.${key}`]; return n; });
                  const v = (value as string) || '';
                  if (key === boundKey(taxContext, SUPPLY_BINDING)) setSupplyType(v);
                  else if (key === boundKey(taxContext, PLACE_BINDING)) setPlaceOfSupplyCode(v);
                  else setTaxFields(prev => ({ ...prev, [key]: value }));
                }} />
            ) : taxContext?.registrationStatus === 'NOT_REGISTERED' ? (
              <div className="form-group"><label className="form-label">Tax</label><div className="form-input" style={{ color: 'var(--text-muted)', background: 'var(--bg-secondary, #f8fafc)' }}>Not registered — no tax on this document</div></div>
            ) : (
              <div className="form-group"><SyutSelect label="Supply Type" options={supplyTypeOptions} value={supplyType} onChange={v => setSupplyType((v as string) || '')} isClearable={false} menuPortalTarget={document.body} /></div>
            )}
            <div className="form-group"><label className="form-label">Reason</label><input className="form-input" value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="Return / correction / discount…" /></div>
            {/* A credit note against an invoice REVERSES it, so it takes that invoice's currency
                and rate — the server enforces this and ignores anything else sent (ADR-0031).
                Offering a picker there would let the user choose something the document would
                not honour, so it is locked and says why. A standalone credit note is free. */}
            {isLinkedToInvoice ? (
              <div className="form-group">
                <label className="form-label">Currency</label>
                <input className="form-input" readOnly style={{ background: 'var(--bg-secondary)' }}
                  value={currencyCode ? `${currencyCode} @ ${exchangeRate}` : '—'} />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                  From the invoice being credited — a reversal must use its original rate
                </div>
              </div>
            ) : (
              <div className="form-group" data-field="currencyCode">
                <SyutSelect
                  label="Currency"
                  isAsync defaultOptions cacheOptions={false}
                  loadOptions={loadCurrencies}
                  seedOptions={currencyCode ? [{ value: currencyCode, label: currencyLabelCache.current.get(currencyCode) || currencyCode }] : undefined}
                  value={currencyCode}
                  onChange={v => onPickCurrency((v as string) || '')}
                  menuPortalTarget={document.body}
                  isClearable={false}
                />
              </div>
            )}
            {currencyCode && currencyCode !== functionalCurrency && !isLinkedToInvoice && (
              <div className="form-group">
                <label className="form-label">Exchange Rate <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>→ {functionalCurrency}</span> <span className="text-danger">*</span></label>
                <input type="number" step="0.000001" min="0" className="form-input" value={exchangeRate}
                  onChange={e => setExchangeRate(e.target.value)}
                  placeholder={`1 ${currencyCode} = ? ${functionalCurrency}`} />
              </div>
            )}
          </div>
          {remainingCreditable != null && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.75rem', borderRadius: 6, background: overCredit ? 'rgba(220,38,38,0.08)' : 'var(--bg-secondary)', border: `1px solid ${overCredit ? '#dc2626' : 'var(--border-color)'}`, fontSize: '0.8rem', color: overCredit ? '#dc2626' : 'var(--text-secondary)' }}>
              Remaining creditable on this invoice <span style={{ color: 'var(--text-muted)' }}>(with any debit notes raised on it)</span>: <strong>{currencyCode} {money(remainingCreditable)}</strong>
              {overCredit && <span> — this credit note ({currencyCode} {money(grandTotal)}) exceeds it.</span>}
            </div>
          )}
          {billTo && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', fontSize: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.5rem', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Credit To: {billTo.partyName}</span>
              {formatAddress(billTo) && <span style={{ color: 'var(--text-secondary)' }}>{formatAddress(billTo)}</span>}
              {billTo.email && <span style={{ color: 'var(--text-secondary)' }}>✉ {billTo.email}</span>}
              {billTo.partyType === 'SUPPLIER' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span className="badge" style={{ background: '#d97706', color: '#fff' }}>Tagged supplier only</span>
                  <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }} disabled={promoting} onClick={promoteToCustomer}>
                    {promoting ? 'Tagging…' : 'Also use as customer'}
                  </button>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Tags the party as customer and supplier, so it shows in the customer picker from now on.</span>
                </span>
              )}
            </div>
          )}
          </>)}

          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span className="form-label" style={{ margin: 0 }}>Lines to credit</span>
              <button className="btn btn-secondary" onClick={addLine} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Add line</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '0.3rem', width: 120 }}>Type</th><th style={{ padding: '0.3rem', width: 220 }}>Item</th>
                <th style={{ padding: '0.3rem', width: 70 }}>Qty</th><th style={{ padding: '0.3rem', width: 90 }}>Rate</th>
                <th style={{ padding: '0.3rem', width: 110, textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.3rem', width: 90, textAlign: 'right' }}>Tax</th>
                <th style={{ padding: '0.3rem', width: 120, textAlign: 'right' }}>Total</th><th style={{ width: 30 }} />
              </tr></thead>
              <tbody>
                {lines.map(l => {
                  const lt = taxByKey[l.key];
                  const amt = parseFloat(l.lineAmount) || 0;
                  const isItem = l.lineType === 'ITEM';
                  // A row reversing an invoice item line keeps that line's item and pack (the server refuses another item).
                  const lockedItem = isItem && !!l.sourceInvoiceLineId;
                  const resolvedLedger = resolvedLedgerOf(l);
                  const freeText = isFreeTextService(l);
                  // A free-text line shows its ledger picker once something is typed on it.
                  const showFreeTextLedger = freeText && (!!l.description.trim() || parseFloat(l.lineAmount) > 0 || !!l.incomeLedgerId);
                  const nonStock = isItem && itemLookups.itemTypeOf(l) === 'NON_STOCK';
                  return (
                  <React.Fragment key={l.key}>
                    <tr style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.25rem' }}><SyutSelect options={LINE_TYPE_OPTIONS} value={isItem ? 'SERVICE' : l.lineType} onChange={v => {
                        const next = (v as string) || 'SERVICE';
                        if (next === (isItem ? 'SERVICE' : l.lineType)) return;
                        // An ITEM row leaving for another type drops its item half too.
                        updLine(l.key, { lineType: next, serviceId: '', description: '', rate: '', lineAmount: '', incomeLedgerId: '', goodsReturned: false, ...blankItemFieldValues() });
                      }} isClearable={false} isDisabled={lockedItem} size="sm" menuPortalTarget={document.body} /></td>
                      <td style={{ padding: '0.25rem' }}>{lockedItem ? (
                        <ItemPickerCell row={l} lookups={itemLookups} onPick={() => {}} locked lockedTitle="The invoice line's item — a return keeps it" />
                      ) : (l.lineType === 'SERVICE' || isItem) ? (
                        <SyutSelect key="product" isAsync loadOptions={loadServicesAndItems} defaultOptions cacheOptions={false}
                          seedOptions={isItem
                            ? (l.itemId ? [{ value: `${ITEM_PREFIX}${l.itemId}`, label: `Item · ${l.itemText || ''}` }] : undefined)
                            : (l.serviceId ? [{ value: `${SERVICE_PREFIX}${l.serviceId}`, label: `Service · ${serviceCache.current.get(l.serviceId)?.name || ''}` }] : undefined)}
                          value={isItem ? (l.itemId ? `${ITEM_PREFIX}${l.itemId}` : '') : (l.serviceId ? `${SERVICE_PREFIX}${l.serviceId}` : '')}
                          onChange={v => onPickProduct(l, (v as string) || '')} isClearable size="sm" menuPortalTarget={document.body}
                          placeholder="Pick service or item" error={errors[`line-${l.key}-item`]} />
                      ) : l.lineType !== 'DISCOUNT' ? <SyutSelect key={l.lineType} isAsync loadOptions={(input: string) => loadServices(input, 'REIMBURSEMENT')} defaultOptions cacheOptions={false}
                        seedOptions={l.serviceId ? [{ value: l.serviceId, label: serviceCache.current.get(l.serviceId)?.name || '' }] : undefined}
                        value={l.serviceId} onChange={v => onPickService(l.key, (v as string) || '')} isClearable size="sm" menuPortalTarget={document.body} placeholder="Pick reimbursement" /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ padding: '0.25rem' }}><input className={`form-input${errors[`line-${l.key}-quantity`] ? ' form-field-error' : ''}`} style={{ fontSize: '0.8125rem' }} type="number" value={l.quantity} title={isItem && l.uomCode ? `In ${l.uomCode}` : undefined} placeholder={isItem ? 'Qty *' : undefined} onChange={e => updLine(l.key, { quantity: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem' }} type="number" value={l.rate} title={isItem && l.uomCode ? `Per ${l.uomCode}` : undefined} onChange={e => updLine(l.key, { rate: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem' }}><input className="form-input" style={{ fontSize: '0.8125rem', textAlign: 'right' }} type="number" value={l.lineAmount} onChange={e => updLine(l.key, { lineAmount: e.target.value })} /></td>
                      <td style={{ padding: '0.25rem', textAlign: 'right', color: 'var(--text-secondary)' }}>{lt ? money(lt.tax) : '—'}</td>
                      <td style={{ padding: '0.25rem', textAlign: 'right', fontWeight: 600 }}>{lt ? money(lt.total) : money(amt)}</td>
                      <td style={{ textAlign: 'center' }}><button title="Remove" onClick={() => removeLine(l.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}><IconSystem.x size={14} /></button></td>
                    </tr>
                    {isItem && l.itemId && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <ItemLineDetails row={l} lookups={itemLookups} onChange={p => updLine(l.key, p)}
                            packLocked={lockedItem}
                            leading={nonStock ? undefined : (
                              <div style={{ alignSelf: 'center', minWidth: 150 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                  title="Ticked: the goods come back into stock. Unticked: a price allowance — nothing moves.">
                                  <input type="checkbox" checked={l.goodsReturned} onChange={e => updLine(l.key, { goodsReturned: e.target.checked })} />
                                  Goods returned
                                </label>
                                {!l.goodsReturned && <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Price allowance — no stock moves</div>}
                              </div>
                            )}
                            showLocation={movesStock(l)}
                            ledger={<ReadOnlyLedgerValue ledger={resolvedLedger} missingText={ITEM_INCOME_LEDGER_MISSING}
                              title={l.sourceLedger?.ledgerId
                                ? 'A return credits back the ledger the invoice line credited'
                                : "Follows the masters — the item's / category's Sales income override, else the entity default"} />}
                            ledgerLabel="Income ledger"
                            errors={{ uom: errors[`line-${l.key}-uom`], location: errors[`line-${l.key}-location`] }} />
                        </td>
                      </tr>
                    )}
                    {l.lineType === 'SERVICE' && (l.serviceId || showFreeTextLedger) && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.4rem 0.5rem' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.75rem', alignItems: 'start', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.4rem 0.6rem' }}>
                            <div style={{ width: 300 }}>
                              {freeText ? (<>
                                <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>Income ledger <span className="text-danger">*</span></label>
                                <SyutSelect isAsync loadOptions={loadLedgers} defaultOptions cacheOptions={false}
                                  seedOptions={l.incomeLedgerId ? [{ value: l.incomeLedgerId, label: ledgerCache.current.get(l.incomeLedgerId) || '' }] : undefined}
                                  value={l.incomeLedgerId} onChange={v => updLine(l.key, { incomeLedgerId: (v as string) || '' })}
                                  isClearable size="sm" menuPortalTarget={document.body} placeholder="Pick the income ledger" error={errors[`line-${l.key}-ledger`]} />
                                <div style={{ fontSize: '0.66rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>No service picked — this line names its own ledger</div>
                              </>) : (<>
                                <label className="form-label" style={{ fontSize: '0.68rem', marginBottom: '0.1rem' }}>Income ledger</label>
                                <ReadOnlyLedgerValue ledger={resolvedLedger} missingText={SERVICE_INCOME_LEDGER_MISSING}
                                  title="Follows the masters — the service's / service category's Sales income ledger, else the entity default" />
                              </>)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={8} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                        <textarea className="form-textarea" rows={descFocusKey === l.key ? 4 : 2}
                          style={{ fontSize: '0.8rem', minHeight: descFocusKey === l.key ? '4.5rem' : '2.9rem', width: '100%', transition: 'min-height 0.12s ease' }}
                          value={l.description} placeholder={isItem ? 'Description… (blank = the item name)' : 'Description…'}
                          onFocus={() => setDescFocusKey(l.key)} onBlur={() => setDescFocusKey(null)}
                          onChange={e => updLine(l.key, { description: e.target.value })} />
                      </td>
                    </tr>
                    {unmappedKeys.includes(l.key) && (isItem ? !!l.itemId : !!l.serviceId) && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 0.25rem 0.5rem 0.5rem' }}>
                          <div className="text-danger" style={{ fontSize: '0.75rem' }}>
                            {isItem
                              ? <>{l.itemText || 'This item'} has no tax mapping under {taxContext?.jurisdiction?.jurisdictionCode || 'the jurisdiction'}. Map its tax on the item, or pick another item; the document cannot be posted until then.</>
                              : <>{serviceCache.current.get(l.serviceId)?.name || 'This service'} has no tax master under {taxContext?.jurisdiction?.jurisdictionCode || 'the jurisdiction'}. Map it under Service Master, or pick another service; the document cannot be posted until then.</>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {errors.lines && <div className="form-error-message">{errors.lines}</div>}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label className="form-label">Narration</label>
              <textarea className="form-textarea" rows={2} value={narration} onChange={e => setNarration(e.target.value)} />
            </div>
            <div style={{ minWidth: 240, fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>{hasItemLines ? 'Subtotal' : 'Fees subtotal'}</span><strong>{money(subtotal)}</strong></div>
              {reimbursementTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Reimbursements</span><strong>{money(reimbursementTotal)}</strong></div>}
              {discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', color: 'var(--text-muted)' }}><span>Discount</span><span>− {money(discountTotal)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax</span><strong>{taxNote ? '—' : money(tax ? tax.tax : 0)}</strong></div>
              {taxNote && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '0 0 0.2rem' }}>{taxNote}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Total Credit</span><strong>{currencyCode} {money(grandTotal)}</strong></div>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || (!!editId && !isDirty)} title={editId && !isDirty ? 'No changes to save' : undefined}><IconSystem.check size={16} /> {editId ? 'Save Changes' : isCopy ? 'Post New Credit Note' : 'Post Credit Note'}</button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={editId ? 'Save Changes' : 'Post Credit Note'}
        message={(placeWarning ? `${placeWarning} ` : '') + (editId
          ? `Re-post this credit note for ${currencyCode} ${money(grandTotal)}? The ledger entries will be updated.`
          : `Post this credit note for ${currencyCode} ${money(grandTotal)}? ${againstInvoiceId ? 'It will reduce the selected invoice’s outstanding.' : 'It will create an on-account credit for the customer.'}`)}
        confirmLabel={editId ? 'Save' : 'Post'} cancelLabel="Cancel" variant="default" onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={discardConfirmOpen} title="Discard changes?"
        message="You have unsaved changes on this credit note. Do you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );
};

// ---- View modal ----
interface CreditNoteViewModalProps {
  creditNote: CreditNoteDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (docNo: string) => void;
}
const CreditNoteViewModal: React.FC<CreditNoteViewModalProps> = ({ creditNote, onClose, tabId, moduleName, onRestoreView }) => {
  const { minimize } = useModalMinimize();
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !creditNote) return;
    const no = creditNote.creditNoteNumber || creditNote.id;
    minimize({ id: `${tabId}-view-modal-${no}`, title: `View - ${no}`, moduleName, tabId, onRestore: () => onRestoreView?.(creditNote.creditNoteNumber || '') });
    onClose();
  }, [tabId, moduleName, creditNote, minimize, onClose, onRestoreView]);

  if (!creditNote) return null;
  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}><h2 className="modal-title">Credit Note {creditNote.creditNoteNumber}</h2><div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>{tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>}<button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button></div></div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Customer</div><strong>{creditNote.partyName}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div>{(creditNote.creditNoteDate || '').slice(0, 10)}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Against Invoice</div>{creditNote.againstInvoiceNumber || <span style={{ color: 'var(--text-muted)' }}>On account</span>}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Supply</div>{creditNote.supplyType}{creditNote.placeOfSupplyCode ? <div style={{ color: 'var(--text-secondary)' }}>Place of supply {creditNote.placeOfSupplyCode}</div> : null}</div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div>{statusBadges(creditNote)}</div>
          </div>
          {creditNote.reason && <div style={{ marginBottom: '0.5rem', fontSize: '0.82rem' }}><span style={{ color: 'var(--text-muted)' }}>Reason: </span>{creditNote.reason}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem', width: 30 }}>#</th><th style={{ padding: '0.3rem', width: 120 }}>Type</th><th style={{ padding: '0.3rem' }}>Item</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 120 }}>Amount</th><th style={{ padding: '0.3rem', textAlign: 'right', width: 110 }}>Tax</th></tr></thead>
            <tbody>{creditNote.lines.map(l => (
              <React.Fragment key={l.id}>
                <tr style={{ borderTop: '1px solid var(--border-color)' }}><td style={{ padding: '0.3rem' }}>{l.lineNo}</td><td style={{ padding: '0.3rem' }}>{l.lineType}</td><td style={{ padding: '0.3rem' }}>{l.lineType === 'ITEM' ? (<>
                  <ItemLineSummary l={{ itemCode: l.itemCode, itemName: l.itemName, quantity: l.quantity, uomCode: l.uomCode }} />
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {l.goodsReturned ? `Goods returned to ${itemLabel(l.locationCode, l.locationName) || 'stock'}` : 'Price allowance — no stock moved'}
                  </div>
                </>) : (l.serviceName || '—')}</td><td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.lineAmount)}</td><td style={{ padding: '0.3rem', textAlign: 'right' }}>{money(l.taxAmount)}</td></tr>
                {l.description && <tr><td /><td colSpan={4} style={{ padding: '0 0.3rem 0.4rem 0.3rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{l.description}</td></tr>}
                {snapshotSummary(l.taxSnapshot) && <tr><td /><td colSpan={4} style={{ padding: '0 0.3rem 0.4rem 0.3rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>{snapshotSummary(l.taxSnapshot)}</td></tr>}
              </React.Fragment>
            ))}</tbody>
          </table>
          <div style={{ marginLeft: 'auto', maxWidth: 260, marginTop: '0.75rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>{creditNote.lines.some(l => l.lineType === 'ITEM') ? 'Subtotal' : 'Fees subtotal'}</span><strong>{money(creditNote.subtotal)}</strong></div>
            {creditNote.reimbursementTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Reimbursements</span><strong>{money(creditNote.reimbursementTotal)}</strong></div>}
            {creditNote.discountTotal > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Discount</span><span>− {money(creditNote.discountTotal)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax</span><strong>{money(creditNote.taxTotal)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderTop: '1px solid var(--border-color)', fontSize: '1rem' }}><span>Total Credit</span><strong>{creditNote.currencyCode} {money(creditNote.grandTotal)}</strong></div>
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </>
  );
};

// ---- Screen ----
// The sales notes register (ADR-0053): credit notes and Sales Debit Notes together, as the tax
// return reports them. A credit note opens in this screen's own modals; a debit note is an
// invoice-class document and opens in the invoice form/view in debit-note mode.
type CnViewMode = 'list' | 'grid';

const NOTE_TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Credit and debit notes' },
  { value: 'CREDIT_NOTE', label: 'Credit notes' },
  { value: 'DEBIT_NOTE', label: 'Debit notes' },
];
const isDn = (r: SalesNoteRegisterRowDto) => r.noteType === 'DEBIT_NOTE';
// From the register row alone: settlement isn't on the row, so the edit handler re-checks the full document.
const rowBlockReason = (r: SalesNoteRegisterRowDto): string | null => {
  if (r.docStatus !== 'POSTED') return r.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (r.isLocked) return 'Locked (filed or closed period)';
  return null;
};
const debitNoteBlockReason = (d: SalesInvoiceDto): string | null => {
  if (d.docStatus !== 'POSTED') return d.docStatus === 'CANCELLED' ? 'Cancelled' : 'Not posted';
  if (d.isLocked) return d.lockSource || 'Locked (filed or closed period)';
  if (d.settlementStatus === 'SETTLED') return 'Fully settled';
  if (d.settlementStatus === 'PARTIAL') return 'Partially settled';
  return null;
};
const noteTypeBadge = (r: SalesNoteRegisterRowDto): React.ReactNode => (
  <span className="badge" style={{ background: isDn(r) ? '#7c3aed' : '#0d9488', color: '#fff' }}>{isDn(r) ? 'Debit' : 'Credit'}</span>
);
const rowStatusBadges = (r: SalesNoteRegisterRowDto): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center', flexWrap: 'wrap' }}>
    <span className="badge">{r.docStatus}</span>
    {r.isLocked && <span className="badge" style={{ background: '#6b7280', color: '#fff' }}>🔒 Locked</span>}
  </span>
);

interface CreditNoteScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const CreditNoteScreen: React.FC<CreditNoteScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('credit-note');
  // A debit note is an invoice-class document: raising, editing and cancelling it are the
  // Sales Invoice permissions (ADR-0053); seeing it here is this screen's VIEW.
  const canDn = useCan('sales-invoice');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [queryParams, setQueryParams] = useState<SalesNoteRegisterQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useSalesNotesRegister(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<CnViewMode>('list');
  const [filterOpen, setFilterOpen] = useState(false);
  const [noteType, setNoteType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  // Credit note modals
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [editCn, setEditCn] = useState<CreditNoteDto | null>(null);
  const [viewCn, setViewCn] = useState<CreditNoteDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Debit note modals (the invoice form / view in debit-note mode)
  const [dnFormOpen, setDnFormOpen] = useState(false);
  const [dnFormMode, setDnFormMode] = useState<'create' | 'edit'>('create');
  const [editDn, setEditDn] = useState<SalesInvoiceDto | null>(null);
  const [viewDn, setViewDn] = useState<SalesInvoiceDto | null>(null);
  const [dnCreateInstanceId, setDnCreateInstanceId] = useState<string>('1');
  const dnMinimizeRef = useRef<(() => void) | null>(null);

  const restoreMinimized = useCallback((id: string | null): boolean => {
    if (!id || !isMinimized(id)) return false;
    const modal = getMinimizedModal(id);
    dismissMinimized(id); modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const handleView = useCallback(async (r: SalesNoteRegisterRowDto) => {
    if (isDn(r)) {
      if (restoreMinimized(tabId ? `${tabId}-view-modal-dn-${r.noteNumber || r.id}` : null)) return;
      try { setViewDn(await salesInvoiceService.getById(r.id)); } catch { notificationService.toast.error('Could not load the debit note.'); }
      return;
    }
    if (restoreMinimized(tabId ? `${tabId}-view-modal-${r.noteNumber || r.id}` : null)) return;
    try { setViewCn(await creditNoteService.getById(r.id)); } catch { notificationService.toast.error('Could not load the credit note.'); }
  }, [tabId, restoreMinimized]);

  // The setup gate (ADR-0048 §3): same capability as the invoice.
  const setupGate = useSetupGate('INVOICING');

  const minimizeOpenForms = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    if (dnFormOpen) dnMinimizeRef.current?.();
  }, [formOpen, dnFormOpen]);

  const handleAddCreditNote = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    minimizeOpenForms();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    setCreateInstanceId(String(next));
    setFormMode('create'); setEditCn(null); setFormOpen(true);
  }, [tabId, isMinimized, setupGate, minimizeOpenForms]);

  const handleAddDebitNote = useCallback(async () => {
    if (!(await setupGate.ensureReady())) return;
    minimizeOpenForms();
    let next = 1;
    if (tabId) while (isMinimized(`${tabId}-form-modal-create-dn-${next}`)) next++;
    setDnCreateInstanceId(String(next));
    setDnFormMode('create'); setEditDn(null); setDnFormOpen(true);
  }, [tabId, isMinimized, setupGate, minimizeOpenForms]);

  const handleEdit = useCallback(async (r: SalesNoteRegisterRowDto) => {
    const blocked = rowBlockReason(r);
    const label = isDn(r) ? 'debit note' : 'credit note';
    if (blocked) { notificationService.toast.warning(`This ${label} cannot be edited — ${blocked}.`); return; }
    if (!(await setupGate.ensureReady())) return;
    if (isDn(r)) {
      if (restoreMinimized(tabId ? `${tabId}-form-modal-edit-dn-${r.noteNumber || r.id}` : null)) return;
      try {
        const full = await salesInvoiceService.getById(r.id);
        const stillBlocked = debitNoteBlockReason(full);
        if (stillBlocked) { notificationService.toast.warning(`This debit note cannot be edited — ${stillBlocked}.`); return; }
        setEditDn(full); setDnFormMode('edit'); setDnFormOpen(true);
      } catch { notificationService.toast.error('Could not load the debit note.'); }
      return;
    }
    if (restoreMinimized(tabId ? `${tabId}-form-modal-edit-${r.noteNumber || r.id}` : null)) return;
    try {
      const full = await creditNoteService.getById(r.id);
      const stillBlocked = editBlockReason(full);
      if (stillBlocked) { notificationService.toast.warning(`This credit note cannot be edited — ${stillBlocked}.`); return; }
      setEditCn(full); setFormMode('edit'); setFormOpen(true);
    } catch { notificationService.toast.error('Could not load the credit note.'); }
  }, [tabId, restoreMinimized, setupGate]);

  // Cross-screen drill-in (e.g. from the Item-wise Sales report): open a credit note
  // by id. Edit when allowed, else read-only view. Caller has already checked scope.
  const handleOpenById = useCallback(async (id: string, mode?: 'edit' | 'view') => {
    try {
      const full = await creditNoteService.getById(id);
      if (mode === 'view') { setViewCn(full); return; }
      const blocked = editBlockReason(full);
      if (blocked) {
        setViewCn(full);
        notificationService.toast.info(`Opened read-only — ${blocked}.`);
        return;
      }
      setEditCn(full); setFormMode('edit'); setFormOpen(true);
    } catch {
      notificationService.toast.error('Could not open the credit note.');
    }
  }, []);

  useNavTarget('credit-note', (t) => handleOpenById(t.recordId, t.mode));

  const handleCopy = useCallback(async (r: SalesNoteRegisterRowDto) => {
    minimizeOpenForms();
    try {
      const full = await creditNoteService.getById(r.id);
      let next = 1;
      if (tabId) while (isMinimized(`${tabId}-form-modal-copy-${next}`)) next++;
      setCopyInstanceId(String(next));
      setEditCn(full); setFormMode('copy'); setFormOpen(true);
    } catch { notificationService.toast.error('Could not load the source credit note.'); }
  }, [tabId, isMinimized, minimizeOpenForms]);

  /** The register row a restored draft/view belongs to — then its full document. */
  const findRow = useCallback((type: 'CREDIT_NOTE' | 'DEBIT_NOTE', docNo?: string) =>
    docNo ? data.find(r => r.noteType === type && r.noteNumber === docNo) : undefined, [data]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, docNo?: string) => {
    if (mode === 'edit' || mode === 'copy') {
      const row = findRow('CREDIT_NOTE', docNo);
      if (row) creditNoteService.getById(row.id).then(setEditCn).catch(() => {});
      if (mode === 'copy' && instanceId) setCopyInstanceId(instanceId);
      setFormMode(mode);
    } else {
      if (instanceId) setCreateInstanceId(instanceId);
      setFormMode('create');
    }
    setFormOpen(true);
  }, [findRow]);

  const handleRestoreView = useCallback((docNo: string) => {
    const row = findRow('CREDIT_NOTE', docNo);
    if (row) creditNoteService.getById(row.id).then(setViewCn).catch(() => {});
  }, [findRow]);

  const handleRestoreDnModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, docNo?: string) => {
    if (mode === 'edit') {
      const row = findRow('DEBIT_NOTE', docNo);
      if (row) salesInvoiceService.getById(row.id).then(setEditDn).catch(() => {});
      setDnFormMode('edit');
    } else {
      if (instanceId) setDnCreateInstanceId(instanceId);
      setDnFormMode('create');
    }
    setDnFormOpen(true);
  }, [findRow]);

  const handleRestoreDnView = useCallback((docNo: string) => {
    const row = findRow('DEBIT_NOTE', docNo);
    if (row) salesInvoiceService.getById(row.id).then(setViewDn).catch(() => {});
  }, [findRow]);

  const handleDelete = useCallback(async (r: SalesNoteRegisterRowDto) => {
    const label = isDn(r) ? 'Debit Note' : 'Credit Note';
    const ok = await confirmDelete({ resourceLabel: r.noteNumber || r.partyName || `this ${label.toLowerCase()}` });
    if (!ok) return;
    try {
      if (isDn(r)) await salesInvoiceService.remove(r.id, r.rowVersion);
      else await creditNoteService.remove(r.id, r.rowVersion);
      notificationService.toast.success(`${label} ${r.noteNumber || ''} cancelled.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || `Could not cancel the ${label.toLowerCase()}.`);
      if (err?.response?.status === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<SalesNoteRegisterRowDto>[] = useMemo(() => [
    { id: 'noteType', header: 'Type', accessorKey: 'noteType', size: 80, cell: ({ row }) => noteTypeBadge(row.original as SalesNoteRegisterRowDto) },
    { id: 'noteNumber', header: 'Note No.', accessorKey: 'noteNumber', size: 130, cell: ({ row }) => (row.original as SalesNoteRegisterRowDto).noteNumber || '—' },
    { id: 'noteDate', header: 'Date', accessorKey: 'noteDate', size: 110, cell: ({ row }) => ((row.original as SalesNoteRegisterRowDto).noteDate || '').slice(0, 10) },
    { id: 'partyName', header: 'Customer', accessorKey: 'partyName', size: 220 },
    {
      id: 'againstInvoiceNumber', header: 'Invoice', accessorKey: 'againstInvoiceNumber', size: 140,
      cell: ({ row }) => {
        const r = row.original as SalesNoteRegisterRowDto;
        return r.againstInvoiceNumber || <span style={{ color: 'var(--text-muted)' }}>{isDn(r) ? 'Stand-alone' : 'On account'}</span>;
      },
    },
    { id: 'reason', header: 'Reason', accessorKey: 'reason', size: 220, cell: ({ row }) => (row.original as SalesNoteRegisterRowDto).reason || '' },
    {
      id: 'grandTotal', header: 'Amount', accessorKey: 'grandTotal', size: 130, align: 'right',
      cell: ({ row }) => { const r = row.original as SalesNoteRegisterRowDto; return <span>{r.currencyCode} {money(r.grandTotal)}</span>; },
    },
    { id: 'status', header: 'Status', accessorKey: 'docStatus', size: 150, cell: ({ row }) => rowStatusBadges(row.original as SalesNoteRegisterRowDto) },
  ], []);

  const rowActions = useCallback((r: SalesNoteRegisterRowDto): ActionMenuItem[] => {
    const perm = isDn(r) ? canDn : can;
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) }];
    if (!isDn(r) && can('CREATE')) items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    if (rowBlockReason(r) === null) {
      if (perm('UPDATE')) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
      if (perm('DELETE')) items.push({ id: 'delete', label: 'Cancel', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, canDn, handleView, handleEdit, handleDelete, handleCopy]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];
  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(refetch)];
  // One "New" menu — the type is chosen before the form opens, and never changes after.
  const newItems: ActionMenuItem[] = [
    ...(can('CREATE') ? [{ id: 'new-cn', label: 'Credit Note', icon: <IconSystem.file size={16} />, onClick: () => { void handleAddCreditNote(); } }] : []),
    ...(canDn('CREATE') ? [{ id: 'new-dn', label: 'Debit Note', icon: <IconSystem.add size={16} />, onClick: () => { void handleAddDebitNote(); } }] : []),
  ];

  const applyFilters = () => {
    setQueryParams(p => ({
      ...p, noteType: (noteType || undefined) as SalesNoteRegisterQueryParams['noteType'],
      fromDate: fromDate || undefined, toDate: toDate || undefined, includeCancelled: includeCancelled || undefined, page: 1,
    }));
    setFilterOpen(false);
  };
  const clearFilters = () => { setNoteType(''); setFromDate(''); setToDate(''); setIncludeCancelled(false); setQueryParams(p => ({ page: 1, pageSize: p.pageSize })); };
  const appliedCount = [noteType, fromDate, toDate, includeCancelled ? '1' : ''].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Credit / Debit Notes"
      description="Credit customers, or raise what they owe after the invoice"
      icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      customToolbarItems={<ToolbarAddMenu id="sales-notes-new" items={newItems} tooltip="Add new — credit or debit note" />}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search number / customer / invoice / reason...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as CnViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Type" options={NOTE_TYPE_OPTIONS} value={noteType} onChange={v => setNoteType((v as string) || '')} isClearable={false} size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate} onChange={e => setToDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No credit or debit notes yet — use New.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
              {data.map(r => (
                <div key={`${r.noteType}-${r.id}`} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', gap: '0.35rem', alignItems: 'center' }}>{noteTypeBadge(r)} {r.noteNumber || '—'} · {(r.noteDate || '').slice(0, 10)}{r.againstInvoiceNumber ? ` · vs ${r.againstInvoiceNumber}` : ''}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', gap: '0.5rem', paddingTop: '0.4rem', borderTop: '1px solid var(--border-color)', alignItems: 'center' }}>
                    {rowStatusBadges(r)}
                    <strong>{r.currencyCode} {money(r.grandTotal)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SetupGateModal gate={setupGate} />
      <CreditNoteFormModal isOpen={formOpen} mode={formMode} creditNote={editCn}
        onSaved={refetch} onClose={() => { setFormOpen(false); setEditCn(null); setFormMode('create'); }}
        tabId={tabId} moduleName={moduleName || 'Credit / Debit Notes'}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId}
        onRestoreModal={handleRestoreModal} minimizeRef={formModalMinimizeRef} />

      <CreditNoteViewModal creditNote={viewCn} onClose={() => setViewCn(null)}
        tabId={tabId} moduleName={moduleName || 'Credit / Debit Notes'} onRestoreView={handleRestoreView} />

      <InvoiceFormModal isOpen={dnFormOpen} mode={dnFormMode} invoice={editDn} documentKind="DEBIT_NOTE"
        onSaved={refetch} onClose={() => { setDnFormOpen(false); setEditDn(null); setDnFormMode('create'); }}
        tabId={tabId} moduleName={moduleName || 'Credit / Debit Notes'}
        createInstanceId={dnCreateInstanceId}
        onRestoreModal={handleRestoreDnModal} minimizeRef={dnMinimizeRef} />

      <InvoiceViewModal invoice={viewDn} onClose={() => setViewDn(null)}
        tabId={tabId} moduleName={moduleName || 'Credit / Debit Notes'} onRestoreView={handleRestoreDnView} />
    </EnterpriseScreenShell>
  );
};

export default CreditNoteScreen;
